import { HierarchyCard, NavigationSlider } from '@yes.technology/react-toolkit'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import {
  ActivityValue,
  FlatActivityGroup,
  FlatActivityModel,
  NestableGroupValue
} from 'types/shared'
import Activity from './Activity'
import styled from 'styled-components'

type ActivityGroupProps = {
  groups: NestableGroupValue
  activityModel: FlatActivityGroup
  activityModels: Record<string, FlatActivityModel>
  parentPath: string[]
}

const Card = styled(HierarchyCard)`
  > div {
    padding: 0px;
  }
`

const TitleWrapper = styled.div`
  padding: 8px;
`

const ContentWrapper = styled.div`
  padding: 8px;
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const hasEmptyValues = (
  values: Record<string, ActivityValue | NestableGroupValue>
): boolean => {
  if (Object.keys(values).length === 0) {
    return true
  }

  return Object.values(values).some((value) => {
    if (Array.isArray(value)) {
      return value.some((group) => {
        return (
          Object.keys(group.values).length === 0 || hasEmptyValues(group.values)
        )
      })
    }

    if (typeof value === 'object') {
      return Object.keys(value).length === 0
    }

    return !value
  })
}

const ActivityGroup = ({
  groups,
  activityModel,
  activityModels,
  parentPath
}: ActivityGroupProps) => {
  const [currentGroup, setCurrentGroup] = useState(1)
  const { t } = useTranslation()

  const visibleActivityIds = useMemo(
    () =>
      activityModel.activity_models.filter(
        (activityId) => !activityModels[activityId]?.hide_in_review
      ),
    [activityModel, activityModels]
  )

  if (!visibleActivityIds.length) {
    return null
  }

  const groupValues = groups[currentGroup - 1]?.values || {}
  const status = hasEmptyValues(groupValues) ? 'attention' : undefined

  return (
    <Card status={status}>
      <TitleWrapper>
        <h2>{activityModel.display_text.des}</h2>
      </TitleWrapper>
      <NavigationSlider
        pulseAnimateOnNext
        currentPosition={currentGroup}
        first={1}
        total={groups.length}
        hierarchyLevel='level_2'
        textForCurrent={t('activity-group.navigation-slider.text-for-current')}
        afterClick={setCurrentGroup}
        heightClass='small'
      />
      <ContentWrapper>
        <Activity
          activityModelIds={visibleActivityIds}
          parentPath={[...parentPath, (currentGroup - 1).toString(), 'values']}
        />
      </ContentWrapper>
    </Card>
  )
}

export default ActivityGroup
